import { type Href, useRouter } from 'expo-router';
import { ArrowLeft } from 'lucide-react-native';
import type { StyleProp, ViewStyle } from 'react-native';
import { colors } from '../../theme/colors';
import { IconButton, type IconButtonSize, type IconButtonVariant } from './IconButton';

const ICON_SIZE_MAP: Record<IconButtonSize, number> = {
  sm: 16,
  md: 18,
  lg: 22,
};

interface BackButtonProps {
  /** Accessible name for the control (e.g. a translated "Go back"). */
  accessibilityLabel: string;
  /**
   * Where the user actually came from, when the screen was told (FLOSEAI-658).
   *
   * Takes precedence over history. A screen reachable from several places
   * cannot infer its origin by popping: expo-router pushes across route groups
   * land in the other group's stack, so `back()` returns to that group's root
   * rather than to the screen the user pressed from. When the caller states the
   * origin it is the only reliable answer, so it wins.
   */
  originHref?: Href;
  /**
   * Logical parent to land on when there is no navigation history to pop
   * (e.g. the screen was opened via a deep link). Keeps the user inside the
   * current flow instead of relying on raw browser history.
   */
  fallbackHref?: Href;
  size?: IconButtonSize;
  variant?: IconButtonVariant;
  tooltip?: string;
  style?: StyleProp<ViewStyle>;
}

export function BackButton({
  accessibilityLabel,
  originHref,
  fallbackHref,
  size = 'md',
  variant = 'subtle',
  tooltip,
  style,
}: BackButtonProps) {
  const router = useRouter();

  const handlePress = () => {
    if (originHref) {
      router.replace(originHref);
      return;
    }
    if (router.canGoBack()) {
      router.back();
      return;
    }
    if (fallbackHref) {
      router.replace(fallbackHref);
      return;
    }
    router.back();
  };

  return (
    <IconButton
      icon={<ArrowLeft size={ICON_SIZE_MAP[size]} color={colors.text.secondary} strokeWidth={2} />}
      variant={variant}
      size={size}
      onPress={handlePress}
      accessibilityLabel={accessibilityLabel}
      tooltip={tooltip}
      style={style}
    />
  );
}
